// Registration Form Validation Utilities

import {
  RegistrationFormData,
  ValidationError,
  ValidationResult,
} from "../types/registration";
import { validatePassword } from "./passwordValidation";

/**
 * Simplified email validation regex.
 * Note: This does NOT fully comply with RFC 5322 and may allow some invalid emails or reject valid ones.
 * For production use, consider using a well-tested email validation library.
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Username validation regex (alphanumeric, underscore, hyphen, 3-20 chars)
 */
const USERNAME_REGEX = /^[a-zA-Z0-9_-]{3,20}$/;

/**
 * Phone validation regex (international format)
 */
const PHONE_REGEX =
  /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;

/**
 * Name validation regex (Unicode letters, spaces, hyphens, apostrophes)
 * Supports ASCII and common extended Latin characters, spaces, hyphens, and apostrophes.
 */
const NAME_REGEX = /^[a-zA-ZÀ-ÿ\s'-]{2,50}$/;

/**
 * Validate email format
 */
export const validateEmail = (email: string): ValidationError | null => {
  if (!email || email.trim() === "") {
    return {
      field: "email",
      message: "Email is required",
      type: "required",
    };
  }

  const trimmedEmail = email.trim();

  if (!EMAIL_REGEX.test(trimmedEmail)) {
    return {
      field: "email",
      message: "Please enter a valid email address",
      type: "format",
    };
  }

  if (trimmedEmail.length > 254) {
    return {
      field: "email",
      message: "Email address is too long (max 254 characters)",
      type: "length",
    };
  }

  return null;
};

/**
 * Validate username
 */
export const validateUsername = (username: string): ValidationError | null => {
  if (!username || username.trim() === "") {
    return {
      field: "username",
      message: "Username is required",
      type: "required",
    };
  }

  const trimmedUsername = username.trim();

  if (trimmedUsername.length < 3) {
    return {
      field: "username",
      message: "Username must be at least 3 characters long",
      type: "length",
    };
  }

  if (trimmedUsername.length > 20) {
    return {
      field: "username",
      message: "Username must not exceed 20 characters",
      type: "length",
    };
  }

  if (!USERNAME_REGEX.test(trimmedUsername)) {
    return {
      field: "username",
      message:
        "Username can only contain letters, numbers, underscores, and hyphens",
      type: "format",
    };
  }

  return null;
};

/**
 * Validate password confirmation
 */
export const validatePasswordConfirmation = (
  password: string,
  confirmation: string
): ValidationError | null => {
  if (!confirmation || confirmation.trim() === "") {
    return {
      field: "passwordConfirmation",
      message: "Password confirmation is required",
      type: "required",
    };
  }

  if (password !== confirmation) {
    return {
      field: "passwordConfirmation",
      message: "Passwords do not match",
      type: "match",
    };
  }

  return null;
};

/**
 * Validate name (first name, last name)
 */
export const validateName = (
  name: string,
  fieldName: string
): ValidationError | null => {
  if (!name || name.trim() === "") {
    return {
      field: fieldName,
      message: `${
        fieldName === "firstName" ? "First name" : "Last name"
      } is required`,
      type: "required",
    };
  }

  const trimmedName = name.trim();

  if (trimmedName.length < 2) {
    return {
      field: fieldName,
      message: `${
        fieldName === "firstName" ? "First name" : "Last name"
      } must be at least 2 characters long`,
      type: "length",
    };
  }

  if (trimmedName.length > 50) {
    return {
      field: fieldName,
      message: `${
        fieldName === "firstName" ? "First name" : "Last name"
      } must not exceed 50 characters`,
      type: "length",
    };
  }

  if (!NAME_REGEX.test(trimmedName)) {
    return {
      field: fieldName,
      message:
        "Name can only contain letters, spaces, hyphens, and apostrophes",
      type: "format",
    };
  }

  return null;
};

/**
 * Validate phone number (optional field)
 */
export const validatePhone = (phone: string): ValidationError | null => {
  if (!phone || phone.trim() === "") {
    return null; // Phone is optional
  }

  const trimmedPhone = phone.trim();

  if (!PHONE_REGEX.test(trimmedPhone)) {
    return {
      field: "phone",
      message: "Please enter a valid phone number",
      type: "format",
    };
  }

  return null;
};

/**
 * Validate terms acceptance
 */
export const validateTermsAcceptance = (
  accepted: boolean
): ValidationError | null => {
  if (!accepted) {
    return {
      field: "acceptTerms",
      message: "You must accept the Terms of Service",
      type: "required",
    };
  }

  return null;
};

/**
 * Validate privacy policy acceptance
 */
export const validatePrivacyAcceptance = (
  accepted: boolean
): ValidationError | null => {
  if (!accepted) {
    return {
      field: "acceptPrivacy",
      message: "You must accept the Privacy Policy",
      type: "required",
    };
  }

  return null;
};

/**
 * Validate entire registration form
 */
export const validateRegistrationForm = (
  formData: RegistrationFormData
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Validate username
  const usernameError = validateUsername(formData.username);
  if (usernameError) errors.push(usernameError);

  // Validate email
  const emailError = validateEmail(formData.email);
  if (emailError) errors.push(emailError);

  // Validate password
  const passwordValidation = validatePassword(formData.password);
  if (!passwordValidation.valid) {
    errors.push({
      field: "password",
      message: passwordValidation.errors.join(". "),
      type: "strength",
    });
  }

  // Validate password confirmation
  const confirmationError = validatePasswordConfirmation(
    formData.password,
    formData.passwordConfirmation
  );
  if (confirmationError) errors.push(confirmationError);

  // Validate first name
  const firstNameError = validateName(formData.firstName, "firstName");
  if (firstNameError) errors.push(firstNameError);

  // Validate last name
  const lastNameError = validateName(formData.lastName, "lastName");
  if (lastNameError) errors.push(lastNameError);

  // Validate phone (optional)
  if (formData.phone) {
    const phoneError = validatePhone(formData.phone);
    if (phoneError) errors.push(phoneError);
  }

  // Validate terms acceptance
  const termsError = validateTermsAcceptance(formData.acceptTerms);
  if (termsError) errors.push(termsError);

  // Validate privacy acceptance
  const privacyError = validatePrivacyAcceptance(formData.acceptPrivacy);
  if (privacyError) errors.push(privacyError);

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get error message for a specific field
 */
export const getFieldError = (
  errors: ValidationError[],
  fieldName: string
): string | undefined => {
  const error = errors.find((err) => err.field === fieldName);
  return error?.message;
};
